import SharedDialog from '../../../shared/shared-dialog';
import {
  ToolsRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { ModuleList } from '../../../shared/analytics/module-list';
import { UserKeys, deprovisionDevices } from '../../../api/users-api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useStore } from '../../state/zustand-store';
import { Dispatch, SetStateAction, useState } from 'react';
import { useAnalytics } from '../../../shared/analytics/create-events';
import { handleErrors } from '../../../utils/errors';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ChromeDeviceModel, Page } from '../../../../types';

import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

interface DeprovisionDeviceDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedDevices: ChromeDeviceModel[];
  setSelectedRows: Dispatch<SetStateAction<string[]>>;
  changeLogRoles: string[];
}

export default function DeprovisionDeviceDialog({
  open,
  handleClose,
  selectedDevices,
  setSelectedRows,
  changeLogRoles,
}: DeprovisionDeviceDialogProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const [deprovisionReason, setDeprovisionReason] = useState('');
  const [hasError, setHasError] = useState(false);
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();

  const deviceIds = selectedDevices.map((device) => device.deviceId);

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      deprovisionDevices(deviceIds, deprovisionReason, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message:
          deviceIds.length > 1
            ? 'Devices deprovisioned successfully!'
            : 'Device deprovisioned successfully!',
      });

      const chromeDeviceQueries = queryClient.getQueriesData<
        Page<ChromeDeviceModel[]>
      >({
        queryKey: [UserKeys.ChromeDevicesPage],
      });

      if (chromeDeviceQueries.length > 0) {
        chromeDeviceQueries.map((chromeDeviceQuery) => {
          const queryKey = chromeDeviceQuery[0];
          const deviceList = chromeDeviceQuery[1];
          if (
            deviceList?.results.some((device) =>
              deviceIds.includes(device.deviceId)
            )
          ) {
            queryClient.setQueryData<Page<ChromeDeviceModel[]>>(
              queryKey,
              (oldData) => {
                if (!oldData) return;

                const newData = {
                  ...oldData,
                  results: oldData.results.map((device) =>
                    deviceIds.includes(device.deviceId)
                      ? {
                          ...device,
                          status: 'DEPROVISIONED',
                        }
                      : device
                  ),
                };
                return newData;
              }
            );
          }
        });
      }

      setSelectedRows([]);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          deviceIds.length > 1
            ? 'There was an issue deprovisioning the devices. Please refresh and try again.'
            : 'There was an issue deprovisioning the device. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.devices.device_management.deprovision_devices, {
        module: ModuleList.modules.devices.name,
        section: ModuleList.modules.devices.sections.chromeDevices,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (deprovisionReason.length === 0) {
      setHasError(true);
    } else {
      setHasError(false);
      if (
        isAbleToSeeUIElement(
          changeLogRoles,
          ToolsRoles.CanDeprovisionDevices.value
        )
      ) {
        setOpenChangeLogDialog(true);
      } else {
        mutate();
      }
    }
  }

  const deprovisionReasons = [
    {
      displayValue: 'Same model replacement',
      value: 'DEPROVISION_REASON_SAME_MODEL_REPLACEMENT',
    },
    {
      displayValue: 'Different model replacement',
      value: 'DEPROVISION_REASON_DIFFERENT_MODEL_REPLACEMENT',
    },
    {
      displayValue: 'Retiring from fleet',
      value: 'DEPROVISION_REASON_RETIRING_DEVICE',
    },
    {
      displayValue: 'ChromeOS Flex upgrade transfer',
      value: 'DEPROVISION_REASON_UPGRADE_TRANSFER',
    },
    {
      displayValue: 'Not required',
      value: 'DEPROVISION_REASON_NOT_REQUIRED',
    },
    {
      displayValue: 'The device was deprovisioned by the Repair Service Center',
      value: 'DEPROVISION_REASON_REPAIR_CENTER',
    },
  ];

  return (
    <>
      <SharedDialog
        open={open}
        handleClose={handleClose}
        title={
          selectedDevices.length > 1
            ? 'Deprovision Devices'
            : 'Deprovision Device'
        }
        dialogContent={
          <Stack gap={2}>
            <Typography>
              {selectedDevices.length > 1 ? (
                <>
                  {'Deprovision '}
                  <strong>{selectedDevices.length} selected devices</strong>
                  {'?'}
                </>
              ) : (
                <>
                  {'Deprovision device number '}
                  <strong>{selectedDevices[0]?.serialNumber}</strong>
                  {'?'}
                </>
              )}
            </Typography>
            <TextField
              select
              id="deprovisionReason"
              error={hasError}
              helperText={hasError ? 'Please provide a reason.' : ' '}
              label="Deprovision Reason"
              fullWidth
              autoComplete="off"
              value={deprovisionReason}
              onChange={(event) => setDeprovisionReason(event.target.value)}
            >
              {deprovisionReasons.map((reason) => (
                <MenuItem key={reason.value} value={reason.value}>
                  {reason.displayValue}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        }
        primaryButton={{
          title:
            selectedDevices.length > 1
              ? 'Deprovision Devices'
              : 'Deprovision Device',
          action: handleSubmit,
          loading: isPending,
          disabled: isPending,
          color: 'error',
        }}
      />
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={mutate}
        mutateLoading={isPending}
      />
    </>
  );
}
